import { Signal, SignalBinding } from "signals";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import Point from "slotworks/utils/geom/point";

export default class SliderElement {

    public onChange: Signal;
    public onInput: Signal;

    private value: number;
    private percent: number;
    private dragging: boolean;
    private trackWidth: number;
    private fillWidth: number;

    private min: number;
    private max: number;
    private step: number;

    private track: PIXI.Sprite;
    private fill: PIXI.Sprite;
    private handle: PIXI.Sprite;

    private mask: PIXI.Graphics;

    private enabled: boolean;

    private orientationListener: SignalBinding;

    constructor(track: PIXI.Sprite, fill: PIXI.Sprite, handle?: PIXI.Sprite) {
        this.onChange = new Signal();
        this.onInput = new Signal();

        this.setEnabled(true);

        this.track = track;
        this.fill = fill;

        this.mask = new PIXI.Graphics();
        this.mask.drawRect(0, 0, 1, 1);
        this.mask.renderable = false;
        this.fill.mask = this.mask;
        this.fill.parent.addChild(this.mask);

        if (handle) {
            this.handle = handle;
            this.handle.anchor.set(0.5, 0);
            this.handle.interactive = true;
            this.handle.buttonMode = true;
            this.handle.on(ButtonEvent.POINTER_DOWN.getPIXIEventString() as any, this.startDrag);
            this.handle.on(ButtonEvent.POINTER_UP.getPIXIEventString() as any, this.stopDrag);
            this.handle.on(ButtonEvent.POINTER_UP_OUTSIDE.getPIXIEventString() as any, this.stopDrag);
            this.handle.on(ButtonEvent.POINTER_MOVE.getPIXIEventString() as any, this.drag);
        }

        this.orientationListener = CanvasManager.onOrientationChange.add(() => this.onOrientationChange());
        this.onOrientationChange();
    }

    public setup(min: number, max: number, step: number) {
        this.min = min;
        this.max = max;
        this.step = step;
        this.value = this.min;
        this.percent = 0;

        this.update();
    }

    public destroy() {
        if (this.track) {
            this.onInput.dispose();
            this.onChange.dispose();
            this.track.destroy();
            this.handle.destroy();
            this.fill.destroy();

            this.onInput = null;
            this.onChange = null;
            this.track = null;
            this.handle = null;
            this.fill = null;
        }

        if (this.orientationListener) {
            this.orientationListener.detach();
            this.orientationListener = null;
        }
    }

    public setValue(value: number) {
        this.percent = (value - this.min) / (this.max - this.min);

        this.percent = Math.min(1, Math.max(0, this.percent));

        this.update();
    }

    public setVisible(visible: boolean) {
        this.track.visible = visible;
        this.fill.visible = visible;
        this.handle.visible = visible;
    }

    public setEnabled(enabled: boolean) {
        this.enabled = enabled;
    }

    private onOrientationChange() {
        this.trackWidth = this.track.width;

        this.mask.x = this.fill.x;
        this.mask.y = this.fill.y;
        this.fillWidth = this.fill.width;
        this.mask.height = this.fill.height;

        this.update();
    }

    private update() {
        this.value = Math.round((this.min + (this.max - this.min) * this.percent) / this.step) * this.step;
        const snappedPercent = (this.value - this.min) / (this.max - this.min);

        this.mask.width = snappedPercent * this.fillWidth;
        this.handle.x = this.track.x + snappedPercent * this.trackWidth;
    }

    private startDrag = () => {
        this.dragging = true;
    }

    private stopDrag = () => {
        if (this.dragging) {
            this.dragging = false;
            this.onChange.dispatch(this.value);
        }
    }

    private drag = (event: PIXI.interaction.InteractionEvent) => {
        if (this.enabled) {
            if (this.dragging) {
                const localMouse = Layers.MenusContent.globalToLocal(new Point(event.data.global.x, event.data.global.y));
                const positionInBar = Math.max(0, Math.min(this.trackWidth, localMouse.x - this.track.x));
                this.percent = positionInBar / this.trackWidth;

                this.update();
                this.onInput.dispatch(this.value);
            }
        }
    }
}
